import * as types from './mutation-types.js'
import teams from '@/services/api/teams'
import { getDeepProperty } from '@/helpers/deepObject.js'

const state = {
  createAction: false,
  createForm: null,
  network: null,
  orgUser: null,
  orgUserNetworks: null,
  orgUserNetworksCount: 0,
  orgUserShows: null,
  orgUserShowsCount: 0,
  orgInvites: null,
  orgInvitesCount: 0,
  orgInviteRolesFilterOptions: [],
  orgNetworks: null,
  orgNetworksCount: 0,
  orgUsers: null,
  orgUsersCount: 0,
  podcastInviteForm: null,
  orgUserRoles: null,
  roleFilterOptions: [],
  teams: null
}

const actions = {
  async addTeamUser ({state}, {teamId, userId}) {
    return teams.addTeamUser(teamId, userId)
  },
  clearOrgUsers ({commit}) {
    commit(types.CLEAR_ORG_USERS)
  },
  clearTeams ({state, commit}) {
    commit(types.CLEAR_TEAMS)
  },
  async createTeam ({state}, payload) {
    return teams.createTeam(state.createAction, payload)
  },
  async createNetwork ({state}, payload) {
    return teams.createNetwork(payload)
  },
  async deleteNetwork ({state}, path) {
    return teams.deleteNetwork(path)
  },
  async deleteOrgUser ({state}, path) {
    return teams.deleteOrgUser(path)
  },
  async deleteTeamUser ({state}, {teamId, userId}) {
    return teams.deleteTeamUser(teamId, userId)
  },
  async getPodcastsInviteForm ({commit}) {
    let result = await teams.getPodcastsInviteForm()

    commit(types.SET_PODCAST_BULK_INVITES, result)
    return result
  },
  async getTeams ({state, commit}, accountId) {
    let result = await teams.getTeams(accountId)

    commit(types.SET_CAN_CREATE_TEAM, result)
    commit(types.SET_TEAM_LIST, result.collection)
    return result
  },
  async getNetwork ({commit}, networkId) {
    let result = await teams.getNetwork(networkId)

    commit(types.SET_NETWORK, result)
    return result
  },
  async getNetworksForm ({commit}) {
    let result = await teams.getNetworksForm()

    commit(types.SET_NETWORK_CREATE, result.create)
    return result
  },
  async getOrgInvites ({state, commit}, {path, options}) {
    const limit = options.limit || 10
    options = { ...options, limit: limit }

    let result = await teams.getOrgInvites(path, options)

    commit(types.SET_ORG_INVITES_COUNT, result.count)
    commit(types.SET_ORG_INVITES, result.collection)
    commit(types.SET_ORG_INVITES_ROLE_OPTIONS, result.filter_options)

    return result
  },
  async getOrgNetworks ({state, commit}, {path, options}) {
    const limit = options.limit || 10
    options = { ...options, limit: limit }

    let result = await teams.getOrgNetworks(path, options)

    commit(types.SET_ORG_NETWORKS_COUNT, result.count)
    commit(types.SET_ORG_NETWORKS, result.collection)

    return result
  },
  async getOrgUsers ({state, commit}, {path, options}) {
    const limit = options.limit || 10
    options = { ...options, limit: limit }

    let result = await teams.getOrgUsers(path, options)

    commit(types.SET_ORG_USERS_COUNT, result.count)
    commit(types.SET_ORG_USERS, result.collection)
    commit(types.SET_ORG_USERS_ROLE_OPTIONS, result.filter_options)

    return result
  },
  async getOrgUser ({state, commit}, {userId, accountId}) {
    let result = await teams.getOrgUser(userId, accountId)
    commit(types.SET_EDIT_ORG_USER, result)
    return result
  },
  async sendBulkPodcastsInvite ({state}, payload) {
    return teams.sendPodcastsInvite(state.podcastInviteForm.action, payload)
  },
  async getOrgUserNetworks ({commit}, {userId, options}) {
    let result = await teams.getOrgUserNetworks(userId, options)
    commit(types.SET_EDIT_ORG_USER_NETWORKS, result.collection)
    commit(types.SET_EDIT_ORG_USER_NETWORKS_COUNT, result.count)
    return result.collection
  },
  async getOrgUserShows ({commit}, {userId, options}) {
    let result = await teams.getOrgUserShows(userId, options)
    commit(types.SET_EDIT_ORG_USER_SHOWS, result.collection)
    commit(types.SET_EDIT_ORG_USER_SHOWS_COUNT, result.count)
    return result.collection
  },
  async reassignUserTeam ({state}, {teamId, userId, payload}) {
    return teams.reassignTeam(teamId, userId, payload)
  },
  async updateTeam ({state}, {path, payload}) {
    return teams.updateTeam(path, payload)
  },
  async updateNetwork ({state}, {path, payload}) {
    return teams.updateNetwork(path, payload)
  },
  async updateUserRole ({state}, {path, payload}) {
    return teams.updateUserRole(path, payload)
  }
}

const mutations = {
  [types.CLEAR_TEAMS] (state) {
    state.teams = null
  },
  [types.CLEAR_ORG_USERS] (state) {
    state.orgUser = null
    state.orgUserShows = null
    state.orgUsers = null
    state.orgUsersCount = 0
    state.roleFilterOptions = []
  },
  [types.SET_EDIT_ORG_USER] (state, user) {
    state.orgUser = user
  },
  [types.SET_EDIT_ORG_USER_NETWORKS] (state, networks) {
    state.orgUserNetworks = networks
  },
  [types.SET_EDIT_ORG_USER_NETWORKS_COUNT] (state, count) {
    state.orgUserNetworksCount = count
  },
  [types.SET_EDIT_ORG_USER_SHOWS] (state, shows) {
    state.orgUserShows = shows
  },
  [types.SET_EDIT_ORG_USER_SHOWS_COUNT] (state, count) {
    state.orgUserShowsCount = count
  },
  [types.SET_ORG_INVITES] (state, invites) {
    state.orgInvites = invites
  },
  [types.SET_ORG_INVITES_COUNT] (state, count) {
    state.orgInvitesCount = count
  },
  [types.SET_ORG_INVITES_ROLE_OPTIONS] (state, options) {
    state.orgInviteRolesFilterOptions = options
  },
  [types.SET_ORG_NETWORKS] (state, networks) {
    state.orgNetworks = networks
  },
  [types.SET_ORG_NETWORKS_COUNT] (state, count) {
    state.orgNetworksCount = count
  },
  [types.SET_ORG_USER_ROLES] (state, roles) {
    state.orgUserRoles = roles
  },
  [types.SET_ORG_USERS] (state, users) {
    state.orgUsers = users
  },
  [types.SET_ORG_USERS_COUNT] (state, count) {
    state.orgUsersCount = count
  },
  [types.SET_ORG_USERS_ROLE_OPTIONS] (state, options) {
    state.roleFilterOptions = options
  },
  [types.SET_PODCAST_BULK_INVITES] (state, form) {
    state.podcastInviteForm = form
  },
  [types.SET_TEAM_LIST] (state, teams) {
    state.teams = teams
  },
  [types.SET_CAN_CREATE_TEAM] (state, team) {
    state.createAction = getDeepProperty(team, 'create.action')
    state.createForm = getDeepProperty(team, 'create.input')
  },
  [types.SET_NETWORK] (state, network) {
    state.network = network
  }
}

const getters = {
  bulkInvitePodcastsForm: state => state.podcastInviteForm,
  canCreateTeam: state => state.createAction,
  canBulkInviteToPodcasts: state => state.podcastInviteForm && state.podcastInviteForm.action,
  editOrgUser: state => state.orgUser,
  editOrgUserRoles: state => state.orgUserRoles,
  editOrgUserNetworks: state => state.orgUserNetworks,
  editOrgUserNetworksCount: state => state.orgUserNetworksCount,
  editOrgUserShows: state => state.orgUserShows,
  editOrgUserShowsCount: state => state.orgUserShowsCount,
  network: state => state.network,
  orgInvites: state => state.orgInvites,
  orgInvitesCount: state => state.orgInvitesCount,
  orgInvitesRoleFilterOptions: state => state.orgInviteRolesFilterOptions,
  orgNetworks: state => state.orgNetworks,
  orgNetworksCount: state => state.orgNetworksCount,
  orgUsers: state => state.orgUsers,
  orgUsersCount: state => state.orgUsersCount,
  orgUsersRoleFilterOptions: state => state.roleFilterOptions,
  teams: state => state.teams,
  teamsCreateForm: state => state.createForm
}

export default {
  state,
  actions,
  mutations,
  getters
}
